import React, { useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { PasswordStrengthMeter } from './LoginScreen';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const PasswordResetConfirm = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score += 20;
    if (password.length >= 12) score += 10;
    if (/[a-z]/.test(password)) score += 10;
    if (/[A-Z]/.test(password)) score += 15;
    if (/\d/.test(password)) score += 15;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 15;
    if (/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password)) score += 15;
    return score;
  };

  const PasswordStrengthMeter = ({ password }) => {
    const getStrengthLabel = (score) => {
      if (score >= 80) return { label: 'Very Strong', color: 'bg-green-500' };
      if (score >= 60) return { label: 'Strong', color: 'bg-blue-500' };
      if (score >= 40) return { label: 'Medium', color: 'bg-yellow-500' };
      if (score >= 20) return { label: 'Weak', color: 'bg-orange-500' };
      return { label: 'Very Weak', color: 'bg-red-500' };
    };

    const strength = calculatePasswordStrength(password);
    const { label, color } = getStrengthLabel(strength);

    return (
      <div className="mt-2 text-left px-2">
        <div className="flex justify-between mb-1">
          <span className="text-xs text-gray-600">Password Strength:</span>
          <span className="text-xs text-gray-600">{label}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded">
          <div className={`h-2 rounded transition-all duration-300 ${color}`} style={{ width: `${strength}%` }} />
        </div>
        <div className="mt-2 text-xs text-gray-500">
          <ul className="space-y-1">
            <li className={password.length >= 8 ? 'text-green-600' : ''}>• Minimum 8 characters</li>
            <li className={/[A-Z]/.test(password) ? 'text-green-600' : ''}>• At least one uppercase letter</li>
            <li className={/[a-z]/.test(password) ? 'text-green-600' : ''}>• At least one lowercase letter</li>
            <li className={/\d/.test(password) ? 'text-green-600' : ''}>• At least one number</li>
            <li className={/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-600' : ''}>• At least one special character</li>
          </ul>
        </div>
      </div>
    );
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const passwordStrength = calculatePasswordStrength(formData.newPassword);
    if (passwordStrength < 40) {
      setError('Password is too weak. Please meet more security criteria.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/reset-password/${uid}/${token}/`,
        { new_password: formData.newPassword }
      );
      
      setMessage(response.data.message || 'Password has been reset successfully');
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reset password. The link may be invalid or expired.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-[#002D43]">
            Reset Your Password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your new password below
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#002D43] focus:border-[#002D43] focus:z-10 sm:text-sm"
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
              />
              {formData.newPassword && <PasswordStrengthMeter password={formData.newPassword} />}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-[#002D43] focus:border-[#002D43] focus:z-10 sm:text-sm"
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {message && (
            <div className="rounded-md bg-green-50 p-4">
              <p className="text-sm text-green-800">{message}</p>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-[#002D43] hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#002D43] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>

          <div className="text-center">
            <a
              href="/login"
              className="font-medium text-[#002D43] hover:text-opacity-80"
            >
              Back to login
            </a>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordResetConfirm;